

	import { Component, Watch, Prop } from 'vue-property-decorator';
	import { BaseView } from '@shared/views/BaseView';
	import { DeckSearchRequest, Company, Category, Artist, Series, Pro, Technology } from '@shared/models';

	@Component({})
	export default class DeckFilterMenu extends BaseView {

		public searchRequest: DeckSearchRequest = new DeckSearchRequest();
		public visible: boolean = false;

		public companyProxy: string = '';
		public recommendedCompanies: Company[] = [];
		public companiesLoading: boolean = false;


		public techProxy: string = '';
		public recommendedTechnologies: Technology[] = [];
		public technologiesLoading: boolean = false;

		public proProxy: string = '';
		public recommendedPros: Pro[] = [];
		public prosLoading: boolean = false;

		public artistProxy: string = '';
		public artistsLoading: boolean = false;
		public recommendedArtists: Artist[] = [];

		public seriesProxy: string = '';
		public recommendedSeries: Series[] = [];
		public seriesLoading: boolean = false;

		public categoryProxy: string = '';
		public recommendedCategories: Category[] = [];
		public categoriesLoading: boolean = false;

		public sortColumnOptions: any[] = [
			{ text: 'Year', value: 'year' },
			{ text: 'Name', value: 'name' },
			{ text: 'Season', value: 'season' },
			{ text: 'Created Date', value: 'createddate' },
			{ text: 'Last Updated', value: null },
		];
		public sortDirectionOptions: any[] = [
			{ text: 'Asc', value: 'asc' },
			{ text: 'Desc', value: 'desc' },
		];
		public activeOptions: any[] = [{ text: 'All', value: null }, { text: 'Inactive', value: false }, { text: 'Active', value: true }];
		public pageSizeOptions: any[] = [
			{ text: '60', value: 60 }, 
			{ text: '120', value: 120 }, 
			{ text: '240', value: 240 }, 
		];
		public $refs!: { criteriaField: any };

		/** Fires when the company select list is changed. Searches companies to display in the list */
		@Watch('companyProxy')
		public async searchCompanies(criteria: string) {
			try {
				this.companiesLoading = true;
				if (!criteria) { return; }
				this.recommendedCompanies = await this.$api.companyService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended companies');
			} finally {
				this.companiesLoading = false;
			}
		}

		/** Fires when the artist select list is changed. Searches artists to display in the list */
		@Watch('artistProxy')
		public async searchArtists(criteria: string) {
			try {
				this.artistsLoading = true;
				if (!criteria) { return; }
				this.recommendedArtists = await this.$api.artistService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended artists');
			} finally {
				this.artistsLoading = false;
			}
		}

		/** Fires when the pro select list is changed. Searches pros to display in the list */
		@Watch('proProxy')
		public async searchPros(criteria: string) {
			try {
				this.prosLoading = true;
				if (!criteria) { return; }
				this.recommendedPros = await this.$api.proService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended pros');
			} finally {
				this.prosLoading = false;
			}
		}

		/** Fires when the series select list is changed. Searches series to display in the list */
		@Watch('seriesProxy')
		public async searchSeries(criteria: string) {
			try {
				this.seriesLoading = true;
				if (!criteria) { return; }
				this.recommendedSeries = await this.$api.seriesService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended series');
			} finally {
				this.seriesLoading = false;
			}
		}

		/** Fires when the categories select list is changed. Searches categories to display in the list */
		@Watch('categoryProxy')
		public async searchCategories(criteria: string) {
			try {
				this.categoriesLoading = true;
				if (!criteria) { return; }
				this.recommendedCategories = await this.$api.categoryService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended categories');
			} finally {
				this.categoriesLoading = false;
			}
		}

		/** Fires when the technologies select list is changed. Searches technologies to display in the list */
		@Watch('techProxy')
		public async searchTechnologies(criteria: string) {
			try {
				this.technologiesLoading = true;
				if (!criteria) { return; }
				this.recommendedTechnologies = await this.$api.technologyService.autocomplete(criteria);
			} catch (err) {
				this.errorHandler.handle(err, 'An error occurred while loading recommended technologies');
			} finally {
				this.technologiesLoading = false;
			}
		}

		/** Applies the specified filter and redirects to the new page with the correct query parameters */
		public filter(resetPage: boolean) {
			this.scroll.scrollToTop();
			if (resetPage) { this.searchRequest.pageIndex = 0; }
			this.searchRequest.companyIds = this.searchRequest.companies.map((x) => x.id);
			this.searchRequest.technologyIds = this.searchRequest.technologies.map((x) => x.id);
			this.searchRequest.artistIds = this.searchRequest.artists.map((x) => x.id);
			this.searchRequest.proIds = this.searchRequest.pros.map((x) => x.id);
			this.searchRequest.categoryIds = this.searchRequest.categories.map((x) => parseInt(x.id.toString(), null));


			// this.analytics.event('Decks', 'click', 'Filter Applied');

			// STORE SELECTED FILTERS IN LOCAL STORAGE
			this.storage.store('deck-filter', this.searchRequest);
			this.$router.push({ name: 'deck-list', query: this.searchRequest.collapse() }).catch(() => { /* INTENTIONALLY EMPTY */ });
		}

		/** Clears the filter that applied to the result set */
		public clear() {
			this.visible = false;
			this.storage.remove('deck-filter');
			this.$router.push('/decks/browse').catch(() => { /* INTENTIONALLY EMPTY */ });
			// this.analytics.event('Decks', 'click', 'Filter Cleared');
		}

		/** Shows this menu. */
		public show(searchRequest: DeckSearchRequest) {
			this.searchRequest = new DeckSearchRequest(searchRequest);
			this.recommendedArtists = this.searchRequest.artists;
			this.recommendedSeries = this.searchRequest.series;
			this.recommendedCategories = this.searchRequest.categories;
			this.recommendedCompanies = this.searchRequest.companies;
			this.recommendedTechnologies = this.searchRequest.technologies;
			this.recommendedPros = this.searchRequest.pros;
			this.visible = true;
			const that = this;
			setTimeout(() => that.$refs.criteriaField.focus(), this.settings.focusTimeout);
			// this.analytics.event('Decks', 'click', 'Filter Menu Opened');
		}

		/** Hides this menu  */
		public hide() {
			this.visible = false;
		}

	}
